import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { peep_client } from '~/src/api/peep';
import { useAbort } from '~src/utils/useAbort';
import { Button, TextInput, Text, Paper, Stack, Title, Alert, SegmentedControl } from '@mantine/core';
import { AlertCircle, Mail } from 'lucide-react';
import { HTTPError } from 'ky';
import { ApiErrorResponseSchema, UserHint } from '~src/types/errors';

// --- Types ---

type SigninMode = 'password' | 'code';

type PasswordState =
  | { status: 'idle'; email: string; password: string }
  | { status: 'submitting'; email: string; password: string }
  | { status: 'email_not_verified'; email: string; password: string }
  | { status: 'resending_verification'; email: string; password: string }
  | { status: 'error'; email: string; password: string; error: string };

type CodeSigninState =
  | { mode: 'enter'; status: 'idle'; code: string }
  | { mode: 'enter'; status: 'submitting'; code: string }
  | { mode: 'enter'; status: 'error'; code: string; error: string }
  | { mode: 'request'; status: 'entering_email'; email: string }
  | { mode: 'request'; status: 'sending'; email: string }
  | { mode: 'request'; status: 'code_sent'; email: string; code: string }
  | { mode: 'request'; status: 'submitting'; email: string; code: string }
  | { mode: 'request'; status: 'error'; email: string; code: string; error: string };

// --- Code Signin Component ---

function CodeSignin() {
  const [state, setState] = useState<CodeSigninState>({ mode: 'enter', status: 'idle', code: '' });
  const navigate = useNavigate();
  const abortRef = useAbort();

  const isLoading = state.status === 'submitting' || state.status === 'sending';

  const switchMode = (newMode: 'enter' | 'request') => {
    if (newMode === 'enter') {
      setState({ mode: 'enter', status: 'idle', code: '' });
    } else {
      setState({ mode: 'request', status: 'entering_email', email: '' });
    }
  };

  const updateCode = (code: string) => {
    setState((s) => {
      if (s.mode === 'enter') {
        return s.status === 'error' ? { mode: 'enter', status: 'idle', code } : { ...s, code };
      } else if (s.status === 'code_sent' || s.status === 'error') {
        return { ...s, code };
      }
      return s;
    });
  };

  const updateEmail = (email: string) => {
    setState((s) => {
      if (s.mode === 'request' && (s.status === 'entering_email' || s.status === 'error')) {
        return { mode: 'request', status: 'entering_email', email };
      }
      return s;
    });
  };

  const requestCode = async () => {
    if (state.mode !== 'request' || state.status === 'sending' || state.status === 'submitting') return;
    const email = state.email;

    setState({ mode: 'request', status: 'sending', email });

    try {
      await peep_client.users.requestMagicLink(email, { signal: abortRef.current.signal });
      setState({ mode: 'request', status: 'code_sent', email, code: '' });
    } catch (e) {
      console.error(e);
      setState({ mode: 'request', status: 'error', email, code: '', error: 'Failed to send code. Please try again.' });
    }
  };

  const submitCode = async () => {
    const code =
      state.mode === 'enter' ? state.code : state.status === 'code_sent' || state.status === 'error' ? state.code : '';

    if (!code) return;

    if (state.mode === 'enter') {
      setState({ mode: 'enter', status: 'submitting', code });
    } else if (state.status === 'code_sent' || state.status === 'error') {
      setState({ mode: 'request', status: 'submitting', email: state.email, code });
    } else {
      return;
    }

    try {
      await peep_client.users.signinWithToken(code, true, { signal: abortRef.current.signal });
      navigate('/feed');
    } catch (e) {
      console.error(e);

      let errorMsg = 'An error occurred. Please try again.';
      if (e instanceof HTTPError) {
        try {
          const body = await e.response.json();
          const parsed = ApiErrorResponseSchema.parse(body);
          if (parsed.message === UserHint.ResourceNotFound) {
            errorMsg = 'Invalid or expired code. Please try again.';
          } else if (parsed.message) {
            errorMsg = parsed.message;
          }
        } catch {
          // Use default error
        }
      }

      if (state.mode === 'enter') {
        setState({ mode: 'enter', status: 'error', code, error: errorMsg });
      } else {
        setState({ mode: 'request', status: 'error', email: state.email, code, error: errorMsg });
      }
    }
  };

  const error = state.status === 'error' ? state.error : null;
  const canSubmit =
    state.mode === 'enter'
      ? state.code && (state.status === 'idle' || state.status === 'error')
      : (state.status === 'code_sent' || state.status === 'error') && state.code;

  return (
    <Stack gap="md">
      <SegmentedControl
        value={state.mode}
        onChange={(value) => switchMode(value as 'enter' | 'request')}
        disabled={isLoading}
        fullWidth
        data={[
          { label: 'Enter code', value: 'enter' },
          { label: 'Request code', value: 'request' },
        ]}
        styles={{
          root: { fontFamily: 'Lato' },
          label: { fontFamily: 'Lato' },
        }}
      />

      {error && (
        <Alert icon={<AlertCircle size={16} />} color="red" style={{ fontFamily: 'Lato' }}>
          {error}
        </Alert>
      )}

      {state.mode === 'enter' ? (
        <TextInput
          placeholder="Enter code from email (e.g. abcd-efgh)"
          value={state.code}
          onChange={(e) => updateCode(e.target.value)}
          disabled={state.status === 'submitting'}
          size="md"
          styles={{
            input: {
              fontFamily: 'Lato',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
            },
          }}
        />
      ) : (
        <>
          <TextInput
            placeholder="Enter your email"
            type="email"
            value={'email' in state ? state.email : ''}
            onChange={(e) => updateEmail(e.target.value)}
            disabled={state.status !== 'entering_email'}
            size="md"
            styles={{
              input: {
                fontFamily: 'Lato',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
              },
            }}
          />
          <Button
            onClick={requestCode}
            disabled={!('email' in state && state.email) || state.status === 'sending' || state.status === 'submitting'}
            loading={state.status === 'sending'}
            variant="light"
            size="md"
            style={{
              fontFamily: 'Lato',
              fontWeight: '600',
              borderRadius: '8px',
            }}
            fullWidth
          >
            {state.status === 'code_sent' || state.status === 'error' ? 'Resend Code' : 'Send Code'}
          </Button>
          {(state.status === 'code_sent' || state.status === 'submitting') && (
            <Alert icon={<Mail size={16} />} color="green" style={{ fontFamily: 'Lato' }}>
              Code sent! Check your email.
            </Alert>
          )}
          {(state.status === 'code_sent' || state.status === 'submitting' || state.status === 'error') && (
            <TextInput
              placeholder="Enter code from email (e.g. abcd-efgh)"
              value={state.code}
              onChange={(e) => updateCode(e.target.value)}
              disabled={state.status === 'submitting'}
              size="md"
              styles={{
                input: {
                  fontFamily: 'Lato',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                },
              }}
            />
          )}
        </>
      )}

      <Button
        onClick={submitCode}
        disabled={!canSubmit}
        loading={state.status === 'submitting'}
        size="md"
        style={{
          backgroundColor: 'var(--global-main)',
          fontFamily: 'Lato',
          fontWeight: '600',
          borderRadius: '8px',
        }}
        fullWidth
      >
        Sign In
      </Button>
    </Stack>
  );
}

// --- Password Signin Component ---

function PasswordSignin() {
  const [state, setState] = useState<PasswordState>({ status: 'idle', email: '', password: '' });
  const navigate = useNavigate();
  const abortRef = useAbort();

  const isLoading = state.status === 'submitting' || state.status === 'resending_verification';

  const updateEmail = (email: string) => {
    setState((s) =>
      s.status === 'submitting' || s.status === 'resending_verification'
        ? s
        : { status: 'idle', email, password: s.password },
    );
  };

  const updatePassword = (password: string) => {
    setState((s) =>
      s.status === 'submitting' || s.status === 'resending_verification'
        ? s
        : { status: 'idle', email: s.email, password },
    );
  };

  const submit = async () => {
    if (state.status === 'submitting' || state.status === 'resending_verification') return;

    const { email, password } = state;
    setState({ status: 'submitting', email, password });

    try {
      await peep_client.users.signin(email, password, true, { signal: abortRef.current.signal });
      navigate('/feed');
    } catch (e) {
      console.error(e);

      if (e instanceof HTTPError) {
        try {
          const body = await e.response.json();
          const parsed = ApiErrorResponseSchema.parse(body);

          if (parsed.message === UserHint.EmailNotVerified) {
            setState({ status: 'email_not_verified', email, password });
          } else if (parsed.message === UserHint.BadCredentials) {
            setState({ status: 'error', email, password, error: 'Invalid email or password. Please try again.' });
          } else {
            setState({
              status: 'error',
              email,
              password,
              error: parsed.message || 'An error occurred. Please try again.',
            });
          }
        } catch {
          setState({ status: 'error', email, password, error: 'An error occurred. Please try again.' });
        }
      } else {
        setState({ status: 'error', email, password, error: 'An error occurred. Please try again.' });
      }
    }
  };

  const resendVerification = async () => {
    if (state.status !== 'email_not_verified') return;

    const { email, password } = state;
    setState({ status: 'resending_verification', email, password });

    try {
      await peep_client.users.requestMagicLink(email, { signal: abortRef.current.signal });
      navigate('/check-email', { state: { email } });
    } catch (e) {
      console.error(e);
      setState({ status: 'error', email, password, error: 'Failed to send verification code. Please try again.' });
    }
  };

  const error =
    state.status === 'error'
      ? state.error
      : state.status === 'email_not_verified'
        ? "Your email is not verified. We'll send you a sign-in code - use it to sign in and verify your account."
        : null;

  const canSubmit = state.email && state.password && (state.status === 'idle' || state.status === 'error');

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<AlertCircle size={16} />} color="red" style={{ fontFamily: 'Lato' }}>
          {error}
        </Alert>
      )}

      {state.status === 'email_not_verified' && (
        <Button
          onClick={resendVerification}
          disabled={state.status !== 'email_not_verified'}
          loading={false}
          leftSection={<Mail size={16} />}
          variant="light"
          size="md"
          style={{
            fontFamily: 'Lato',
            fontWeight: '600',
            borderRadius: '8px',
          }}
          fullWidth
        >
          Send Verification Code
        </Button>
      )}

      {state.status === 'resending_verification' && (
        <Button
          disabled
          loading
          leftSection={<Mail size={16} />}
          variant="light"
          size="md"
          style={{
            fontFamily: 'Lato',
            fontWeight: '600',
            borderRadius: '8px',
          }}
          fullWidth
        >
          Send Verification Code
        </Button>
      )}

      <TextInput
        placeholder="Enter your email"
        type="email"
        value={state.email}
        onChange={(e) => updateEmail(e.target.value)}
        disabled={isLoading}
        size="md"
        styles={{
          input: {
            fontFamily: 'Lato',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
          },
        }}
      />
      <TextInput
        placeholder="Enter your password"
        type="password"
        value={state.password}
        onChange={(e) => updatePassword(e.target.value)}
        disabled={isLoading}
        size="md"
        styles={{
          input: {
            fontFamily: 'Lato',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
          },
        }}
      />

      <Stack gap="sm">
        <div style={{ textAlign: 'right' }}>
          <Button
            onClick={() => navigate('/forgot-password')}
            variant="subtle"
            size="xs"
            style={{
              fontFamily: 'Lato',
              color: 'var(--global-main)',
              padding: '0',
              height: 'auto',
            }}
          >
            Forgot password?
          </Button>
        </div>

        <Button
          onClick={submit}
          disabled={!canSubmit}
          loading={state.status === 'submitting'}
          size="md"
          style={{
            backgroundColor: 'var(--global-main)',
            fontFamily: 'Lato',
            fontWeight: '600',
            borderRadius: '8px',
          }}
          fullWidth
        >
          Sign In
        </Button>
      </Stack>
    </Stack>
  );
}

// --- Main Signin Page ---

export default function Signin() {
  const [mode, setMode] = useState<SigninMode>('password');
  const navigate = useNavigate();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        minHeight: '100dvh',
        backgroundColor: 'var(--global-gray-lighter-7)',
        padding: '2rem',
      }}
    >
      <Paper
        style={{
          width: 'min(400px, 90vw)',
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid #f0f0f0',
        }}
      >
        <Stack gap="lg">
          <div style={{ textAlign: 'center' }}>
            <Title order={2} style={{ fontFamily: 'Lato', color: '#333', marginBottom: '0.5rem' }}>
              Welcome Back
            </Title>
            <Text size="sm" style={{ fontFamily: 'Lato', color: '#666' }}>
              Sign in to your account
            </Text>
          </div>

          <SegmentedControl
            value={mode}
            onChange={(value) => setMode(value as SigninMode)}
            fullWidth
            data={[
              { label: 'With password', value: 'password' },
              { label: 'With email code', value: 'code' },
            ]}
            styles={{
              root: { fontFamily: 'Lato' },
              label: { fontFamily: 'Lato' },
            }}
          />

          {mode === 'password' ? <PasswordSignin /> : <CodeSignin />}

          <Button
            onClick={() => navigate('/signup')}
            variant="subtle"
            size="md"
            style={{
              fontFamily: 'Lato',
              color: 'var(--global-main)',
            }}
            fullWidth
          >
            Don't have an account? Sign up
          </Button>
        </Stack>
      </Paper>
    </div>
  );
}
